import styled from "@emotion/styled";
import {
  type ClassificationDefinition,
  type ClassificationValue,
} from "@repo/api";
import { LightMode } from "@repo/skapa";
import { Text } from "@repo/skapa";
import { Tooltip } from "@repo/skapa";
import { t } from "@repo/translation";
import { translateClassification } from "@repo/ui";
import { pixels } from "@repo/ui";
import { FlexDiv } from "@repo/ui";

interface ClassificationProps {
  id: string;
  classification: number | ClassificationDefinition;
  showLabel?: boolean;
}

const ClassificationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  & > * {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: ${pixels.space10};
  }
`;

interface ClassificationDotProps {
  color: string;
}

const ClassificationDot = styled.div<ClassificationDotProps>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(props) => props.color};
`;

const classifications = {
  Urgent: 4,
  High: 3,
  Medium: 2,
  Low: 1,
} as Record<ClassificationDefinition, ClassificationValue>;

const isClassificationValue = (val: number): val is ClassificationValue => {
  if (val > 0 && val < 5) {
    return true;
  }

  return false;
};

const colors: Record<ClassificationValue, string> = {
  1: LightMode.colourSemanticInformative,
  2: LightMode.colourSemanticCaution,
  3: LightMode.colourSemanticCaution,
  4: LightMode.colourSemanticNegative,
};

export const Classification = ({
  id,
  classification,
  showLabel,
}: ClassificationProps) => {
  const classificationValue =
    typeof classification === "string"
      ? classifications[classification]
      : isClassificationValue(classification)
        ? classification
        : 2; // Default to medium if unknown value
  const color = colors[classificationValue];
  const dots: any[] = [];

  for (let i = 0; i < 4; i++) {
    if (i < classificationValue) {
      dots.push(<ClassificationDot key={i} color={color} />);
    }
  }

  return (
    <ClassificationContainer>
      <Tooltip
        describedById={id}
        tooltipText={`${t("label.classification")}: ${translateClassification(
          classification
        )}`}
        position="trailing"
      >
        <FlexDiv flexDirection="column" gap="3px">
          {dots.map((d) => d)}
        </FlexDiv>
      </Tooltip>
      {showLabel && <Text>{translateClassification(classification)}</Text>}
    </ClassificationContainer>
  );
};
